import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import type { StreakReward } from '@/api/client';
import { useAudio } from '@/hooks/useAudio';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Full-screen celebration shown when a streak milestone unlocks a reward.
 * Stack multiple rewards by passing them one at a time — the caller is
 * responsible for queuing.
 */
export default function RewardModal({
  reward,
  onDismiss,
}: {
  reward: StreakReward | null;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const audio = useAudio();
  const { haptic } = useTelegram();

  // Ring + buzz exactly when a reward starts showing — adds the
  // "ding!" moment that makes the achievement feel earned.
  useEffect(() => {
    if (!reward) return;
    audio.play('bell');
    haptic?.notificationOccurred('success');
  }, [reward, audio, haptic]);

  return (
    <AnimatePresence>
      {reward && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center
                     bg-night-950/85 backdrop-blur-sm px-6"
          onClick={onDismiss}
        >
          <motion.div
            initial={{ scale: 0.85, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 18 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-sm rounded-3xl border border-gold-500/50
                       bg-night-800/90 px-7 py-8 text-center shadow-[0_0_60px_rgba(232,195,86,0.25)]"
          >
            {/* Soft golden glow halo around the icon */}
            <motion.div
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.15, type: 'spring', stiffness: 180, damping: 16 }}
              className="mx-auto mb-4 flex h-20 w-20 items-center justify-center
                         rounded-full bg-gold-500/15 text-5xl"
              style={{ filter: 'drop-shadow(0 0 18px rgba(232, 195, 86, 0.55))' }}
            >
              {reward.icon}
            </motion.div>

            <p className="mb-2 text-[10px] uppercase tracking-[0.25em] text-gold-500/70">
              ✦ {t('streak.reward_title')} ✦
            </p>

            <h2 className="mb-3 font-serif text-2xl leading-tight text-gold-200">
              {reward.title}
            </h2>

            <p className="mb-6 font-serif text-sm italic leading-relaxed text-gold-300/80">
              {reward.blurb}
            </p>

            <button
              type="button"
              onClick={onDismiss}
              className="btn-primary w-full"
            >
              {t('streak.reward_dismiss')}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
